import React, { ReactNode } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import EnhancedButton from './EnhancedButton';

type Props = {
  title: string;
  loading?: boolean;
  open: boolean;
  onConfirm?: () => any;
  onCancel: () => any;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
  children: ReactNode;
  allowOverflowY?: boolean;
  errorText?: string;
  maxWidth?: DialogProps['maxWidth'];
  onClose?: () => void;
  enableBackdropBlur?: boolean;
};

/**
 * Renders a generic dialog
 * @param title
 * @param open
 * @param onConfirm
 * @param onCancel
 * @param onClose
 * @param confirmLabel
 * @param cancelLabel
 * @param confirmDisabled
 * @param cancelDisabled
 * @param children
 * @param allowOverflowY
 * @param errorText
 * @param loading
 * @param maxWidth
 * @param enableBackdropBlur - Enables backdrop blur effect
 * @constructor
 */
const GenericDialog = ({
  title,
  open,
  onConfirm,
  onCancel,
  onClose,
  confirmLabel,
  cancelLabel,
  confirmDisabled,
  cancelDisabled,
  children,
  allowOverflowY,
  errorText,
  loading,
  maxWidth,
  enableBackdropBlur = false,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose || onCancel}
      fullScreen={isSmall}
      fullWidth
      maxWidth={maxWidth}
      sx={{ overflowY: allowOverflowY ? 'unset' : undefined }}
      slotProps={{
        backdrop: enableBackdropBlur
          ? {
              sx: {
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(4px)',
              },
            }
          : undefined,
      }}
    >
      <DialogTitle>
        {title}
        {loading && (
          <CircularProgress sx={{ float: 'right' }} color={'primary'} />
        )}
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      {errorText && (
        <DialogContent sx={{ color: 'red' }}>{errorText}</DialogContent>
      )}
      <DialogActions sx={{ p: theme.spacing(3) }}>
        <Button
          size={'large'}
          color={'secondary'}
          onClick={onCancel}
          disabled={cancelDisabled}
        >
          {cancelLabel || t('cancel')}
        </Button>
        {onConfirm && (
          <EnhancedButton
            color={'primary'}
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {confirmLabel || t('save')}
          </EnhancedButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenericDialog;
